import { useState, useRef, useEffect, useCallback } from 'react'
import { Box } from '@mui/material'
import { L } from '../ui/LavDropdown'
import { StIc, StPanel, StToggle, StNote } from './stBits'

/**
 * SettingsReminders — desktop "Settings · Reminders" design (Claude Design
 * Settings.html): a schedule strip + three collapsible reminder cards with
 * timing choices, SMS/email channels, {token} pill editors and live previews.
 *
 * Same data model and defaults as the legacy AutomatedRemindersSettings — no
 * new features. Save/Discard are driven by the parent Settings save bar:
 *   - onChange(data) reports the DB-shaped payload (parent's handleSave uses it)
 *   - hasUnsavedChangesRef mirrors dirty state (parent shows the save bar)
 *   - onCancelRef exposes a reset-to-saved handler (parent's Discard calls it)
 */

// ── tokens (legacy {placeholder} names → friendly labels) ──
const TOKENS = [
	{ tok: 'petName', label: 'Pet name' },
	{ tok: 'customerName', label: 'Client name' },
	{ tok: 'employeeName', label: 'Groomer' },
	{ tok: 'appointmentDate', label: 'Date' },
	{ tok: 'appointmentTime', label: 'Time' },
	{ tok: 'serviceName', label: 'Service' },
	{ tok: 'price', label: 'Price' },
	{ tok: 'merchantName', label: 'Business name' },
	{ tok: 'merchantAddress', label: 'Address' },
]
const TOKEN_LABEL = Object.fromEntries(TOKENS.map((t) => [t.tok, t.label]))

const SAMPLE = {
	petName: 'Biscuit', customerName: 'Maya', employeeName: 'Priya',
	appointmentDate: 'Sat, Jul 25', appointmentTime: '10:00 AM',
	serviceName: 'Full Groom', price: '$85', merchantName: 'Whisker & Wag',
	merchantAddress: '2481 Fillmore St',
}

// ── timing metadata (minutes) — identical options to the legacy component ──
const META = [
	{ id: 'r1', n: 1, key: 'reminder_1', title: 'First reminder', desc: 'A last-minute nudge shortly before the appointment.', opts: [30, 60, 120] },
	{ id: 'r2', n: 2, key: 'reminder_2', title: 'Second reminder', desc: 'A heads-up the day or two before.', opts: [720, 1440, 2880] },
	{ id: 'r3', n: 3, key: 'reminder_3', title: 'Third reminder', desc: 'An early reminder so clients can plan ahead.', opts: [4320, 5760, 7200, 8640, 10080] },
]

function whenLabel(m) {
	if (m == null) return 'Off'
	if (m < 60) return `${m} minutes`
	if (m <= 2880) { const h = m / 60; return h === 1 ? '1 hour' : `${h} hours` }
	return `${m / 1440} days`
}

// ── default templates (verbatim from the legacy component) ──
const DEFAULTS = {
	reminder_1: {
		sms: "Hi {customerName}, {petName}'s {serviceName} appointment at {merchantName} is today at {appointmentTime}. We'll see you soon!",
		emailSubject: "Hi {customerName}, {petName}'s appointment is coming up today at {appointmentTime}",
		emailBody: "<p>Hi {customerName},</p><p></p><p>Just a friendly reminder that your appointment for {petName}'s {serviceName} is today at {appointmentTime} at {merchantName}.</p><p>We look forward to seeing you shortly!</p><p></p><p>Warm regards, </p><p>{merchantName}</p>",
		minutes: 60,
	},
	reminder_2: {
		sms: "Hi {customerName}, reminder that {petName}'s {serviceName} is tomorrow on {appointmentDate} at {appointmentTime}. See you soon! - {merchantName}",
		emailSubject: "Tomorrow's appointment for {petName} at {merchantName}",
		emailBody: "<p>Hi {customerName},</p><p></p><p>This is a friendly reminder that {petName}'s {serviceName} is scheduled for {appointmentDate} {appointmentTime} at {merchantName}.</p><p>We're looking forward to seeing you soon!</p><p></p><p>Warm regards, </p><p>{merchantName}</p>",
		minutes: 1440,
	},
	reminder_3: {
		sms: "Hi {customerName}, friendly reminder that {petName}'s {serviceName} at {merchantName} is scheduled for {appointmentDate} at {appointmentTime}.",
		emailSubject: "Reminder for {petName}'s upcoming appointment at {merchantName}",
		emailBody: "<p>Hi {customerName},</p><p></p><p>We're excited to see {petName} soon! This is a reminder that their {serviceName} is scheduled for {appointmentDate} {appointmentTime} at {merchantName}.</p><p>If you have any notes or preferences for this visit, feel free to give us a call at (xxx) xxx-xxxx.</p><p></p><p>Warm regards, </p><p>{merchantName}</p>",
		minutes: 10080,
	},
}

// Build the per-reminder state from a merchant row (null field → default).
function buildFromMerchant(merchant) {
	const out = {}
	for (const m of META) {
		const d = DEFAULTS[m.key]
		const sub = merchant?.[`${m.key}_email_subject`]
		const body = merchant?.[`${m.key}_email_body`]
		const sms = merchant?.[`${m.key}_sms_template`]
		out[m.id] = {
			on: merchant?.[`${m.key}_enabled`] || false,
			when: merchant?.[`${m.key}_minutes`] ?? d.minutes,
			emailSubject: sub == null ? d.emailSubject : sub,
			emailBody: body == null ? d.emailBody : body,
			sms: sms == null ? d.sms : sms,
		}
	}
	return out
}

// Map per-reminder state → DB payload shape (same as legacy onChange).
function toPayload(rem) {
	const p = {}
	for (const m of META) {
		const r = rem[m.id]
		p[`${m.key}_enabled`] = r.on
		p[`${m.key}_minutes`] = r.when || null
		p[`${m.key}_email_subject`] = r.emailSubject
		p[`${m.key}_email_body`] = r.emailBody
		p[`${m.key}_sms_template`] = r.sms
	}
	return p
}

// ── {token} string ⇄ HTML with contentEditable pill spans ──
const escHtml = (t) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
const tokSpan = (tok) => `<span class="rem-tok" data-tok="${tok}" contenteditable="false">${TOKEN_LABEL[tok] || tok}</span>`
function tokensToHtml(str) {
	return String(str || '').split(/(\{\w+\})/g).map((part) => {
		const m = part.match(/^\{(\w+)\}$/)
		if (m) return tokSpan(m[1])
		return escHtml(part).replace(/\n/g, '<br>')
	}).join('')
}
function htmlToTokens(root) {
	let out = ''
	const walk = (node) => {
		node.childNodes.forEach((ch) => {
			if (ch.nodeType === 3) { out += ch.nodeValue; return }
			if (ch.nodeType !== 1) return
			if (ch.dataset && ch.dataset.tok) { out += `{${ch.dataset.tok}}`; return }
			if (ch.tagName === 'BR') { out += '\n'; return }
			if (ch.tagName === 'DIV' || ch.tagName === 'P') { if (out && !out.endsWith('\n')) out += '\n'; walk(ch); return }
			walk(ch)
		})
	}
	walk(root)
	return out.replace(/ /g, ' ')
}
// HTML-mode helpers: the email body is stored as HTML with {token} text, but
// edited with contentEditable pill spans. Convert in both directions so the
// saved value never contains a pill's display label (e.g. "Pet name").
const TOKEN_RE = /\{(\w+)\}/g
export function htmlWithPills(html) {
	const tpl = document.createElement('template')
	tpl.innerHTML = html || ''
	const walker = document.createTreeWalker(tpl.content, NodeFilter.SHOW_TEXT)
	const textNodes = []
	while (walker.nextNode()) textNodes.push(walker.currentNode)
	for (const node of textNodes) {
		if (!TOKEN_RE.test(node.nodeValue)) { TOKEN_RE.lastIndex = 0; continue }
		TOKEN_RE.lastIndex = 0
		const frag = document.createDocumentFragment()
		node.nodeValue.split(/(\{\w+\})/g).forEach((part) => {
			const m = part.match(/^\{(\w+)\}$/)
			if (m) {
				const span = document.createElement('span')
				span.className = 'rem-tok'
				span.setAttribute('data-tok', m[1])
				span.setAttribute('contenteditable', 'false')
				span.textContent = TOKEN_LABEL[m[1]] || m[1]
				frag.appendChild(span)
			} else if (part) frag.appendChild(document.createTextNode(part))
		})
		node.parentNode.replaceChild(frag, node)
	}
	return tpl.innerHTML
}
export function pillsToTokenHtml(root) {
	const clone = root.cloneNode(true)
	clone.querySelectorAll('[data-tok]').forEach((el) => el.replaceWith(document.createTextNode(`{${el.dataset.tok}}`)))
	return clone.innerHTML
}
const resolve = (str) => String(str || '').replace(/\{(\w+)\}/g, (m, k) => (SAMPLE[k] != null ? SAMPLE[k] : m))
function resolveHtml(val) {
	let html = /</.test(val || '') ? val : tokensToHtml(val)
	html = html.replace(/<span[^>]*data-tok="(\w+)"[^>]*>[\s\S]*?<\/span>/g, (m, t) => (SAMPLE[t] != null ? SAMPLE[t] : ''))
	return html.replace(/\{(\w+)\}/g, (m, k) => (SAMPLE[k] != null ? SAMPLE[k] : m))
}

const editorSx = (single) => ({
	minHeight: single ? 0 : 92, border: `1.5px solid ${L.border}`, borderRadius: '10px',
	bgcolor: '#FFFFFF', p: single ? '9px 12px' : '10px 12px', fontFamily: L.font,
	fontSize: '13.5px', fontWeight: 500, lineHeight: single ? 1.4 : 1.7, color: L.text,
	outline: 'none', overflowWrap: 'break-word',
	...(single ? { whiteSpace: 'nowrap', overflowX: 'auto' } : {}),
	'&:focus': { borderColor: L.primary },
	'&:empty::before': { content: 'attr(data-ph)', color: '#B9B2CC', fontWeight: 500 },
	'& .rem-tok': {
		bgcolor: L.primarySoft, color: L.primaryDeep, borderRadius: '6px', p: '2px 6px',
		fontSize: '12px', fontWeight: 700, whiteSpace: 'nowrap',
	},
})

// ── contentEditable token editor (uncontrolled, synced from `value`) ──
function TokenEditor({ value, onChange, mode = 'text', single = false, placeholder, apiRef }) {
	const ref = useRef(null)
	const saved = useRef(null)

	const serialize = useCallback(() => (mode === 'html' ? pillsToTokenHtml(ref.current) : htmlToTokens(ref.current)), [mode])
	const toHtml = (v) => ((mode === 'html' && /</.test(v || '')) ? htmlWithPills(v) : tokensToHtml(v))

	useEffect(() => {
		if (ref.current && serialize() !== (value || '')) ref.current.innerHTML = toHtml(value || '')
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value])

	const remember = () => {
		const sel = window.getSelection()
		if (sel.rangeCount && ref.current && ref.current.contains(sel.anchorNode)) saved.current = sel.getRangeAt(0).cloneRange()
	}
	const emit = () => onChange(serialize())

	const insert = (tok) => {
		const el = ref.current
		if (!el) return
		el.focus()
		const span = document.createElement('span')
		span.className = 'rem-tok'
		span.setAttribute('data-tok', tok)
		span.contentEditable = 'false'
		span.textContent = TOKEN_LABEL[tok] || tok
		const sel = window.getSelection()
		let range = (saved.current && el.contains(saved.current.startContainer)) ? saved.current : null
		if (!range) { range = document.createRange(); range.selectNodeContents(el); range.collapse(false) }
		range.deleteContents()
		range.insertNode(span)
		const space = document.createTextNode(' ')
		span.after(space)
		const nr = document.createRange()
		nr.setStartAfter(space)
		nr.collapse(true)
		sel.removeAllRanges()
		sel.addRange(nr)
		saved.current = nr.cloneRange()
		emit()
	}
	const exec = (cmd) => { if (!ref.current) return; ref.current.focus(); document.execCommand(cmd); emit() }

	useEffect(() => { if (apiRef) apiRef.current = { insert, exec } })

	return (
		<Box
			ref={ref}
			component="div"
			contentEditable
			suppressContentEditableWarning
			data-ph={placeholder || ''}
			onInput={() => { remember(); emit() }}
			onKeyUp={remember}
			onMouseUp={remember}
			onBlur={remember}
			onKeyDown={single ? (e) => { if (e.key === 'Enter') e.preventDefault() } : undefined}
			sx={editorSx(single)}
		/>
	)
}

function TokenChips({ onInsert }) {
	return (
		<Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', mt: '10px' }}>
			<Box component="span" sx={{ fontSize: '11.5px', fontWeight: 650, color: L.muted, mr: '3px' }}>Insert</Box>
			{TOKENS.map((t) => (
				<Box
					component="button"
					type="button"
					key={t.tok}
					onMouseDown={(e) => e.preventDefault()}
					onClick={() => onInsert(t.tok)}
					sx={{
						border: `1px solid ${L.border}`, borderRadius: '99px', bgcolor: '#FFFFFF', p: '5px 11px',
						fontSize: '12px', fontWeight: 650, color: '#6E6886', fontFamily: L.font, cursor: 'pointer',
						'&:hover': { borderColor: L.primary, color: L.primaryDeep, bgcolor: L.primarySoft },
					}}
				>
					{t.label}
				</Box>
			))}
		</Box>
	)
}

// ── schedule strip ──
function Timeline({ rem }) {
	const nodes = META.slice().reverse()
	const connector = { content: '""', position: 'absolute', top: 14, height: 2, bgcolor: '#EAE4F6' }
	return (
		<Box sx={{ display: 'flex', p: '20px 20px 18px' }}>
			{[...nodes.map((m) => ({ ...m, appt: false })), { id: 'appt', appt: true }].map((node, i, arr) => {
				const isFirst = i === 0
				const isLast = i === arr.length - 1
				const r = node.appt ? null : rem[node.id]
				const on = node.appt || (r && r.on)
				const dotSx = node.appt
					? { bgcolor: L.primaryDeep, border: 'none', color: '#FFFFFF' }
					: on
						? { bgcolor: L.primary, border: 'none', color: '#FFFFFF', boxShadow: '0 2px 8px rgba(124,107,217,0.3)' }
						: { bgcolor: '#FFFFFF', border: '1.5px dashed #C9C2E4', color: L.muted }
				return (
					<Box key={node.id} sx={{
						flex: 1, minWidth: 0, position: 'relative', display: 'flex', flexDirection: 'column',
						alignItems: 'center', gap: '8px',
						...(!isFirst && { '&::before': { ...connector, left: 0, right: 'calc(50% + 21px)' } }),
						...(!isLast && { '&::after': { ...connector, left: 'calc(50% + 21px)', right: 0 } }),
					}}>
						<Box sx={{
							width: 30, height: 30, flex: 'none', borderRadius: '50%', display: 'flex',
							alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 800,
							position: 'relative', zIndex: 1, ...dotSx,
						}}>
							{node.appt ? <StIc name="calendar" size={14} sw={1.7} /> : node.n}
						</Box>
						<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px', textAlign: 'center', minWidth: 0 }}>
							<Box component="strong" sx={{ fontSize: '12.5px', fontWeight: 750, color: (node.appt || on) ? L.text : L.muted }}>
								{node.appt ? 'Appointment' : (r.on ? `${whenLabel(r.when)} before` : 'Off')}
							</Box>
							<Box component="span" sx={{ fontSize: '11.5px', fontWeight: 600, color: L.muted }}>
								{node.appt ? 'Day of visit' : (r.on ? '' : 'Not scheduled')}
							</Box>
						</Box>
					</Box>
				)
			})}
		</Box>
	)
}

// ── one reminder card ──
function ReminderCard({ meta, r, upd, bizName }) {
	const [chan, setChan] = useState('sms')
	const [view, setView] = useState('write')
	const [open, setOpen] = useState(false)
	const smsApi = useRef(null)
	const bodyApi = useRef(null)

	const smsResolved = resolve(r.sms)
	const smsSegs = Math.max(1, Math.ceil(smsResolved.length / 160))

	const segBtnSx = (on) => ({
		display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '7px', border: 'none',
		borderRadius: '8px', p: '9px 14px', bgcolor: on ? '#FFFFFF' : 'transparent',
		fontSize: '13px', fontWeight: on ? 700 : 650, color: on ? L.primaryDeep : L.muted,
		fontFamily: L.font, cursor: 'pointer', boxShadow: on ? '0 1px 3px rgba(46,42,64,0.1)' : 'none',
		'& svg': { flex: 'none' },
	})
	const toolBtnSx = {
		width: 28, height: 28, border: 'none', borderRadius: '7px', bgcolor: 'transparent', color: '#6E6886',
		display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', cursor: 'pointer',
		fontFamily: L.font, '&:hover': { bgcolor: L.surface, color: L.text },
	}
	const lbl = (extra = {}) => ({ fontSize: '12px', fontWeight: 650, color: L.text, ...extra })

	return (
		<Box component="section" sx={{
			bgcolor: '#FFFFFF', border: `1px solid ${L.border}`, borderRadius: '14px',
			boxShadow: '0 1px 3px rgba(46,42,64,0.04)', fontFamily: L.font, overflow: 'hidden',
		}}>
			<Box
				role="button"
				tabIndex={0}
				aria-expanded={open}
				onClick={() => setOpen(!open)}
				onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(!open) } }}
				sx={{
					display: 'flex', alignItems: 'center', gap: '13px', p: '16px 20px', cursor: 'pointer',
					'&:hover': { bgcolor: '#FCFBFE' },
				}}
			>
				<Box sx={{
					width: 34, height: 34, flex: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center',
					justifyContent: 'center', fontSize: '14px', fontWeight: 800,
					bgcolor: r.on ? L.primarySoft : L.surface, color: r.on ? L.primaryDeep : L.muted,
				}}>
					{meta.n}
				</Box>
				<Box sx={{ minWidth: 0, flex: 1 }}>
					<Box sx={{ fontSize: '14.5px', fontWeight: 750, letterSpacing: '-0.01em', color: L.text }}>{meta.title}</Box>
					<Box sx={{ fontSize: '12.5px', fontWeight: 600, color: L.muted, mt: '2px' }}>{meta.desc}</Box>
				</Box>
				<Box sx={{ display: 'flex', color: L.muted, flex: 'none', transition: 'transform .18s', transform: open ? 'none' : 'rotate(-90deg)' }}>
					<StIc name="down" size={16} sw={1.8} />
				</Box>
				<Box onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', alignItems: 'center', pl: '13px', ml: '3px', borderLeft: '1px solid #EFEBF7', flex: 'none' }}>
					<StToggle on={r.on} onChange={() => upd({ on: !r.on })} />
				</Box>
			</Box>

			{open && (
				<Box sx={{ borderTop: '1px solid #EFEBF7', p: '18px 20px 20px', bgcolor: r.on ? '#FFFFFF' : '#FCFBFE' }}>
					{!r.on && (
						<Box sx={{
							display: 'flex', alignItems: 'center', gap: '8px', mb: '14px', p: '9px 12px',
							borderRadius: '10px', bgcolor: L.surface, color: '#6E6886', fontSize: '12.5px', fontWeight: 650,
							'& svg': { color: L.muted, flex: 'none' },
						}}>
							<StIc name="info" size={14} sw={1.7} />
							<span>This reminder is off — edit it now, then turn it on when you're ready to start sending.</span>
						</Box>
					)}

					<Box sx={{ ...lbl(), mb: '9px' }}>When to send</Box>
					{/* auto-fit wraps: all-in-a-row on the wide desktop panel, 2–3 per
					    row on a narrow mobile section. */}
					<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(96px, 1fr))', gap: '8px' }}>
						{meta.opts.map((m) => {
							const on = r.when === m
							return (
								<Box
									component="button"
									type="button"
									key={m}
									onClick={() => upd({ when: m })}
									sx={{
										display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left',
										p: '10px 12px', borderRadius: '11px', bgcolor: on ? '#F7F5FC' : '#FFFFFF',
										border: `1.5px solid ${on ? L.primary : '#EAE6F3'}`, cursor: 'pointer', fontFamily: L.font,
										'&:hover': { borderColor: on ? L.primary : '#D8CEF2' },
									}}
								>
									<Box component="span" sx={{ fontSize: '13.5px', fontWeight: 750, color: on ? L.primaryDeep : L.text }}>
										{whenLabel(m)} before
									</Box>
								</Box>
							)
						})}
					</Box>

					<Box sx={{ height: '1px', bgcolor: '#EFEBF7', my: '18px' }} />

					<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: '12px', flexWrap: 'wrap' }}>
						<Box sx={{ display: 'inline-flex', gap: '3px', bgcolor: L.surface, border: `1px solid ${L.border}`, borderRadius: '10px', p: '3px' }}>
							<Box component="button" type="button" onClick={() => setChan('sms')} sx={segBtnSx(chan === 'sms')}>
								<StIc name="message" size={14} sw={1.6} /><span>Text message</span>
							</Box>
							<Box component="button" type="button" onClick={() => setChan('email')} sx={segBtnSx(chan === 'email')}>
								<StIc name="mail" size={14} sw={1.6} /><span>Email</span>
							</Box>
						</Box>
						<Box sx={{ flex: 1 }} />
						<Box
							component="button"
							type="button"
							onClick={() => setView(view === 'write' ? 'preview' : 'write')}
							sx={{
								display: 'inline-flex', alignItems: 'center', gap: '7px', height: 30, px: '13px', flex: 'none',
								borderRadius: '99px', border: `1.5px solid ${L.border}`, bgcolor: '#FFFFFF', color: L.primaryDeep,
								fontFamily: L.font, fontSize: '12.5px', fontWeight: 700, cursor: 'pointer',
								'& svg': { color: L.primary }, '&:hover': { bgcolor: L.primarySoft, borderColor: L.primary },
							}}
						>
							{view === 'write'
								? <><StIc name="eye" size={14} sw={1.6} /><span>Preview</span></>
								: <><StIc name="edit" size={14} sw={1.7} /><span>Edit message</span></>}
						</Box>
					</Box>

					{view === 'write' && chan === 'sms' && (
						<Box>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '6px', minHeight: 24 }}>
								<Box component="span" sx={lbl()}>Message</Box>
								<Box component="span" sx={{ ml: 'auto', fontSize: '11.5px', fontWeight: 650, color: L.muted, fontVariantNumeric: 'tabular-nums' }}>
									≈ {smsResolved.length} characters · {smsSegs} {smsSegs > 1 ? 'texts' : 'text'}
								</Box>
							</Box>
							<TokenEditor apiRef={smsApi} value={r.sms} placeholder="Write the text message…" onChange={(v) => upd({ sms: v })} />
							<TokenChips onInsert={(t) => smsApi.current && smsApi.current.insert(t)} />
						</Box>
					)}

					{view === 'write' && chan === 'email' && (
						<Box>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '6px', minHeight: 24 }}>
								<Box component="span" sx={lbl()}>Subject</Box>
							</Box>
							<TokenEditor value={r.emailSubject} single placeholder="Subject line…" onChange={(v) => upd({ emailSubject: v })} />
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: '12px', mb: '6px', minHeight: 24 }}>
								<Box component="span" sx={lbl()}>Body</Box>
								<Box sx={{ flex: 1 }} />
								<Box sx={{ display: 'flex', gap: '2px' }}>
									<Box component="button" type="button" title="Bold" onMouseDown={(e) => e.preventDefault()} onClick={() => bodyApi.current && bodyApi.current.exec('bold')} sx={toolBtnSx}><strong>B</strong></Box>
									<Box component="button" type="button" title="Italic" onMouseDown={(e) => e.preventDefault()} onClick={() => bodyApi.current && bodyApi.current.exec('italic')} sx={toolBtnSx}><em>I</em></Box>
									<Box component="button" type="button" title="Underline" onMouseDown={(e) => e.preventDefault()} onClick={() => bodyApi.current && bodyApi.current.exec('underline')} sx={toolBtnSx}><Box component="span" sx={{ textDecoration: 'underline' }}>U</Box></Box>
									<Box component="button" type="button" title="Bulleted list" onMouseDown={(e) => e.preventDefault()} onClick={() => bodyApi.current && bodyApi.current.exec('insertUnorderedList')} sx={toolBtnSx}><StIc name="list" size={15} sw={1.4} /></Box>
								</Box>
							</Box>
							<TokenEditor apiRef={bodyApi} value={r.emailBody} mode="html" placeholder="Write the email…" onChange={(v) => upd({ emailBody: v })} />
							<TokenChips onInsert={(t) => bodyApi.current && bodyApi.current.insert(t)} />
						</Box>
					)}

					{view === 'preview' && (
						<Box sx={{ pt: '2px' }}>
							{chan === 'sms' ? (
								<Box sx={{
									display: 'inline-block', maxWidth: 440, bgcolor: '#F3F0FA', color: L.text,
									p: '11px 14px', borderRadius: '16px 16px 16px 5px', fontSize: '13.5px', fontWeight: 500,
									lineHeight: 1.55, whiteSpace: 'pre-wrap',
								}}>
									{smsResolved}
								</Box>
							) : (
								<Box sx={{ border: `1.5px solid ${L.border}`, borderRadius: '12px', maxWidth: 620, overflow: 'hidden' }}>
									<Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', p: '11px 14px', bgcolor: '#FBFAFE', borderBottom: '1px solid #EFEBF7' }}>
										<Box component="strong" sx={{ fontSize: '13.5px', fontWeight: 750 }}>{resolve(r.emailSubject)}</Box>
										<Box component="span" sx={{ fontSize: '11.5px', fontWeight: 600, color: L.muted }}>{bizName || 'Your business'} · to client email</Box>
									</Box>
									<Box
										sx={{ p: '13px 14px', fontSize: '13.5px', lineHeight: 1.65, '& ul': { m: '6px 0', pl: '20px' }, '& p': { m: '0 0 8px' } }}
										dangerouslySetInnerHTML={{ __html: resolveHtml(r.emailBody) }}
									/>
								</Box>
							)}
						</Box>
					)}
				</Box>
			)}
		</Box>
	)
}

export default function SettingsReminders({ merchant, onChange, onCancelRef, hasUnsavedChangesRef, textingActive, onGoMessaging }) {
	const [rem, setRem] = useState(() => buildFromMerchant(merchant))
	const [original, setOriginal] = useState(() => buildFromMerchant(merchant))

	// Re-initialize whenever the merchant row changes (initial load + after save).
	useEffect(() => {
		if (!merchant) return
		const init = buildFromMerchant(merchant)
		setRem(init)
		setOriginal(init)
	}, [merchant])

	const dirty = JSON.stringify(rem) !== JSON.stringify(original)

	// Report DB-shaped payload to the parent (drives handleSave).
	useEffect(() => {
		if (onChange) onChange(toPayload(rem))
	}, [rem, onChange])

	// Mirror dirty state so the parent save bar appears.
	useEffect(() => {
		if (hasUnsavedChangesRef) hasUnsavedChangesRef.current = dirty
	}, [dirty, hasUnsavedChangesRef])

	// Discard hook — parent's Discard button calls this to reset to saved.
	useEffect(() => {
		if (onCancelRef) onCancelRef.current = () => setRem(original)
	}, [onCancelRef, original])

	const upd = (id) => (patch) => setRem((d) => ({ ...d, [id]: { ...d[id], ...patch } }))
	const onCount = META.filter((m) => rem[m.id].on).length

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', fontFamily: L.font }}>
			{!textingActive && (
				<StNote variant="warn">
					Texting isn't set up yet, so text reminders won't send — email reminders still go out.{' '}
					{onGoMessaging ? (
						<Box
							component="button"
							type="button"
							onClick={onGoMessaging}
							sx={{
								border: 'none', bgcolor: 'transparent', color: '#8F6217', fontWeight: 750, cursor: 'pointer',
								textDecoration: 'underline', p: 0, ml: '4px', fontSize: 'inherit', fontFamily: 'inherit',
							}}
						>
							Set up texting
						</Box>
					) : (
						<span> Claim a number in the Messaging section.</span>
					)}
				</StNote>
			)}

			<StPanel
				title="Reminder schedule"
				subtitle="When each reminder goes out before every appointment."
				flush
				action={
					<Box component="span" sx={{
						display: 'inline-flex', alignItems: 'baseline', gap: '5px', bgcolor: L.primarySoft,
						color: L.primaryDeep, borderRadius: '99px', p: '5px 12px', fontSize: '12px', fontWeight: 700,
						whiteSpace: 'nowrap', flex: 'none',
					}}>
						<Box component="strong" sx={{ fontSize: '14.5px', fontWeight: 800 }}>{onCount}</Box>
						<span>of 3 on</span>
					</Box>
				}
			>
				<Timeline rem={rem} />
			</StPanel>

			{META.map((meta) => (
				<ReminderCard key={meta.id} meta={meta} r={rem[meta.id]} upd={upd(meta.id)} bizName={merchant?.name} />
			))}
		</Box>
	)
}
